import { useCallback, useEffect, useRef, useState } from 'react';

import LucideSymbolIcon from '../icons/LucideSymbolIcon';
import type { PublicTarifCategoryDisplay } from '../../utils/academiaTarifs';
import '../../styles/publicTarifCards.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

type CategoryTheme = {
  variant: 'purple' | 'teal' | 'navy';
  shape: string;
  image: string;
  imageAlt: string;
  photoFit?: 'contain' | 'cover';
};

const CATEGORY_THEME: Record<PublicTarifCategoryDisplay['id'], CategoryTheme> = {
  JEUNESSE_PRIORITAIRE: {
    variant: 'purple',
    shape: 'J',
    image: `${PUBLIC_URL}/images/tarif-jeunesse.png`,
    imageAlt: 'Jeune souriant avec un ordinateur portable',
  },
  ADULTE_STANDARD: {
    variant: 'teal',
    shape: 'A',
    image: `${PUBLIC_URL}/images/tarif-adulte.png`,
    imageAlt: 'Parent et enfant devant un ordinateur',
  },
  FONCTIONNAIRE_EMPLOYE: {
    variant: 'navy',
    shape: 'E',
    image: `${PUBLIC_URL}/images/tarif-fonctionnaire.png`,
    imageAlt: 'Professionnel en tenue de bureau',
  },
};

type Props = {
  categories: PublicTarifCategoryDisplay[];
  eyebrow: string;
  title: string;
  subtitle: string;
  loading?: boolean;
};

export default function PublicTarifCategoriesCarousel({
  categories,
  eyebrow,
  title,
  subtitle,
  loading = false,
}: Props) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncCarousel = useCallback(() => {
    const el = trackRef.current;
    if (!el || categories.length === 0) return;

    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollBack(el.scrollLeft > 4);
    setCanScrollForward(el.scrollLeft < maxScroll - 4);

    const slides = Array.from(el.querySelectorAll<HTMLElement>('.public-tarif-programs__slide'));
    if (slides.length === 0) return;

    const viewportCenter = el.scrollLeft + el.clientWidth / 2;
    let nearest = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;
    slides.forEach((slide, index) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const distance = Math.abs(slideCenter - viewportCenter);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = index;
      }
    });
    setActiveIndex(nearest);
  }, [categories.length]);

  useEffect(() => {
    syncCarousel();
    const el = trackRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', syncCarousel, { passive: true });
    const ro = new ResizeObserver(syncCarousel);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', syncCarousel);
      ro.disconnect();
    };
  }, [syncCarousel, categories.length]);

  const scrollByPage = (direction: -1 | 1) => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * el.clientWidth, behavior: 'smooth' });
  };

  const scrollToIndex = (index: number) => {
    const el = trackRef.current;
    if (!el) return;
    const slide = el.querySelectorAll<HTMLElement>('.public-tarif-programs__slide')[index];
    if (!slide) return;
    el.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  };

  const showNav = categories.length > 1;

  return (
    <div className={`public-tarif-programs ${loading ? 'is-loading' : ''}`}>
      <header className="public-tarif-programs__header">
        <p className="text-xs font-bold uppercase tracking-widest text-primary">{eyebrow}</p>
        <h2 className="mt-2 text-3xl font-black text-slate-900 sm:text-4xl">{title}</h2>
        <p className="mt-3 max-w-3xl text-on-surface-variant">{subtitle}</p>
      </header>

      {categories.length > 0 ? (
        <>
          <div className="public-tarif-programs__carousel">
            {showNav ? (
              <button
                type="button"
                className="public-tarif-programs__nav public-tarif-programs__nav--prev"
                onClick={() => scrollByPage(-1)}
                disabled={!canScrollBack}
                aria-label="Catégorie précédente"
              >
                <LucideSymbolIcon name="arrow_back" tone="inherit" size={24} />
              </button>
            ) : null}

            <div
              ref={trackRef}
              className="public-tarif-programs__track"
              role="list"
              aria-label="Catégories de participants"
            >
              {categories.map((cat) => {
                const theme = CATEGORY_THEME[cat.id];
                return (
                  <article
                    key={cat.id}
                    className={`public-tarif-programs__slide public-tarif-programs-card public-tarif-programs-card--${theme.variant}`}
                    role="listitem"
                  >
                    <div className="public-tarif-programs-card__shape" aria-hidden>
                      {theme.shape}
                    </div>

                    <div className="public-tarif-programs-card__body">
                      <div className="public-tarif-programs-card__copy">
                        {cat.highlight ? (
                          <span className="public-tarif-programs-card__badge">Populaire</span>
                        ) : null}
                        <h3 className="public-tarif-programs-card__title">{cat.label}</h3>
                        <p className="public-tarif-programs-card__meta">{cat.age}</p>
                        <p className="public-tarif-programs-card__fees">
                          Inscription&nbsp;: <strong>{cat.inscription}</strong>
                          <br />
                          Participation&nbsp;: <strong>{cat.participation}</strong>
                        </p>
                      </div>
                    </div>

                    <div className="public-tarif-programs-card__photo-wrap" aria-hidden>
                      <img
                        className={`public-tarif-programs-card__photo ${
                          theme.photoFit === 'cover' ? 'is-cover' : ''
                        }`}
                        src={theme.image}
                        alt={theme.imageAlt}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </article>
                );
              })}
            </div>

            {showNav ? (
              <button
                type="button"
                className="public-tarif-programs__nav public-tarif-programs__nav--next"
                onClick={() => scrollByPage(1)}
                disabled={!canScrollForward}
                aria-label="Catégorie suivante"
              >
                <LucideSymbolIcon name="arrow_forward" tone="inherit" size={24} />
              </button>
            ) : null}
          </div>

          {showNav ? (
            <nav className="public-tarif-programs__dots" aria-label="Pagination du carrousel">
              {categories.map((cat, index) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`public-tarif-programs__dot ${index === activeIndex ? 'is-active' : ''}`}
                  onClick={() => scrollToIndex(index)}
                  aria-label={`Catégorie ${index + 1}`}
                  aria-current={index === activeIndex ? 'true' : undefined}
                />
              ))}
            </nav>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
